import { create } from 'zustand';

const ONBOARDING_KEY = 'swarnim_onboarding_complete';

// Distinguish between a user who finished the tour vs. one who skipped it.
// This lets us show a "resume tour" option or track completion analytics later.
type OnboardingStatus = 'complete' | 'skipped' | null;

function readStatus(): OnboardingStatus {
  const raw = localStorage.getItem(ONBOARDING_KEY);
  if (raw === 'complete' || raw === 'skipped') return raw;
  return null;
}

interface OnboardingState {
  readonly isActive: boolean;
  readonly currentStep: number;
  // Reactive boolean — components can subscribe to this directly
  readonly isComplete: boolean;
  readonly start: () => void;
  readonly next: (role?: TourRole) => void;
  readonly skip: () => void;
}

export const TOUR_STEPS_BY_ROLE = {
  employee: [
    {
      title: 'Welcome to Swarnim Track!',
      message: 'Track your daily trips and monitor your field activity all in one place.',
      icon: '👋',
    },
    {
      title: 'Start a Trip',
      message: 'Tap "New Trip" or use the + button to start GPS tracking your journey.',
      icon: '🗺️',
    },
    {
      title: 'View Your History',
      message: 'Check "My Trips" to see all your past and active trips with status updates.',
      icon: '📋',
    },
    {
      title: 'Stay Notified',
      message: 'Get real-time notifications when trips are approved, rejected, or need attention.',
      icon: '🔔',
    },
    {
      title: "You're All Set!",
      message: 'Start tracking your first trip. Your manager will be able to monitor and approve it.',
      icon: '🚀',
    },
  ],
  manager: [
    {
      title: 'Welcome, Manager!',
      message: 'Review and approve your team\'s field trips from one central dashboard.',
      icon: '👋',
    },
    {
      title: 'Approve Trips',
      message: 'Open "Approvals" to review pending trips from your team. You can approve or reject with a note.',
      icon: '✅',
    },
    {
      title: 'Live Map',
      message: 'Watch your team\'s active field employees move in real time on the Live Map.',
      icon: '🗺️',
    },
    {
      title: 'Reports',
      message: 'View daily, weekly, and monthly KM reports for your team and export CSV/PDF.',
      icon: '📊',
    },
    {
      title: "You're All Set!",
      message: 'Your team\'s trips are waiting for review. Head to Approvals to get started.',
      icon: '🚀',
    },
  ],
  admin: [
    {
      title: 'Welcome, Admin!',
      message: 'You have full control — manage employees, branches, clients, and all trip data.',
      icon: '👋',
    },
    {
      title: 'Manage Employees',
      message: 'Add employees one by one or bulk-import via CSV. Assign managers and branches.',
      icon: '👥',
    },
    {
      title: 'Approve & Resolve',
      message: 'Approve trips company-wide and resolve employee disputes from the Disputes page.',
      icon: '⚙️',
    },
    {
      title: 'Audit Trail',
      message: 'Every write action is logged. Use the Audit page to trace any change in the system.',
      icon: '🔍',
    },
    {
      title: "You're All Set!",
      message: 'Start by seeding your branch data and importing your first batch of employees.',
      icon: '🚀',
    },
  ],
  hr: [
    {
      title: 'Welcome, HR!',
      message: 'Access full KM data and HR profiles for all employees across all branches.',
      icon: '👋',
    },
    {
      title: 'Employee Profiles',
      message: 'View and edit HR profiles — vehicle type, DOB, join date, address, emergency contact.',
      icon: '📁',
    },
    {
      title: 'Reports & Exports',
      message: 'Generate monthly KM reports by employee, department, or branch and export to CSV/PDF.',
      icon: '📊',
    },
    {
      title: "You're All Set!",
      message: 'Head to the Reports page to pull your first monthly KM summary.',
      icon: '🚀',
    },
  ],
} as const;

export type TourRole = keyof typeof TOUR_STEPS_BY_ROLE;

// Default to employee steps when role is unknown (e.g. before auth resolves)
export function getTourSteps(role?: TourRole) {
  return TOUR_STEPS_BY_ROLE[role ?? 'employee'];
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  isActive: false,
  currentStep: 0,
  // Initialise from localStorage so the value is reactive from the start
  isComplete: readStatus() !== null,

  start: () => {
    if (get().isComplete) return;
    set({ isActive: true, currentStep: 0 });
  },

  next: (role?: TourRole) => {
    const { currentStep } = get();
    const steps = getTourSteps(role);
    if (currentStep >= steps.length - 1) {
      localStorage.setItem(ONBOARDING_KEY, 'complete');
      set({ isActive: false, currentStep: 0, isComplete: true });
    } else {
      set({ currentStep: currentStep + 1 });
    }
  },

  skip: () => {
    localStorage.setItem(ONBOARDING_KEY, 'skipped');
    set({ isActive: false, currentStep: 0, isComplete: true });
  },
}));
